import React from 'react';
import { Trans } from 'react-i18next';
import { Box, Checkbox, Icon, Link, Text, Title } from '@nimbus-ds/components';
import {
  IdCardIcon,
  LightbulbIcon,
  MobileIcon,
  UserCircleIcon,
} from '@nimbus-ds/icons';
import { DesktopCard } from 'commons/components';
import { useTranslationWithPrefix } from 'commons/utils';

interface ProofOfLifeContentProps {
  readonly isValidationChecked: boolean;
  readonly onCheckboxChange: (checked: boolean) => void;
}

function InfoBox({
  icon,
  textKey,
  t,
}: Readonly<{
  icon: React.ReactNode;
  textKey: string;
  t: (key: string) => string;
}>) {
  return (
    <Box display="flex" flexDirection="row" alignItems="center" gap="3">
      <Icon source={icon} color="primary-interactive" />
      <Text color="neutral-textLow">{t(textKey)}</Text>
    </Box>
  );
}

function ProofOfLifeContent({
  isValidationChecked,
  onCheckboxChange,
}: ProofOfLifeContentProps): JSX.Element {
  const { t } = useTranslationWithPrefix('identityValidation.proofOfLife');

  return (
    <>
      <DesktopCard>
        <Box display="flex" flexDirection="column" gap="6">
          <Title as="h5" fontWeight="regular">
            {t('subtitle')}
          </Title>
          <Title as="h5" fontWeight="bold">
            {t('recomendations')}
          </Title>
          <Box display="flex" flexDirection="column" gap="4">
            <InfoBox icon={<IdCardIcon />} textKey="haveIdReady" t={t} />
            <InfoBox icon={<LightbulbIcon />} textKey="getGoodlight" t={t} />
            <InfoBox
              icon={<UserCircleIcon />}
              textKey="dontUseAccessories"
              t={t}
            />
            <InfoBox icon={<MobileIcon />} textKey="faceInsideCircle" t={t} />
          </Box>
        </Box>
      </DesktopCard>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        marginTop="6"
        gap="2"
      >
        <Checkbox
          aria-label="validation-checkbox"
          name="validation-checkbox"
          checked={isValidationChecked}
          onChange={(event) => onCheckboxChange(event.target.checked)}
        />
        <Text color="neutral-textLow">
          <Trans
            t={t}
            i18nKey="consentmentDescription"
            components={{
              PrivacyDLocalLink: (
                <Link
                  as="a"
                  appearance="primary"
                  href={t('privacyDLocalLink')}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {''}
                </Link>
              ),
            }}
          />
        </Text>
      </Box>
    </>
  );
}

export default ProofOfLifeContent;
